import React, { useEffect, useMemo, useState } from 'react';

const EMPTY_FORM = {
  email: '',
  password: '',
  role: 'user',
  siteIds: [],
};

function UsersSettings() {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const sortedSites = useMemo(() => {
    return [...sites].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [sites]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/users/me/account-users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to load account users');
      setUsers(data.users || []);
      setSites(data.sites || []);
      setDrafts({});
    } catch (err) {
      setError(err.message || 'Failed to load account users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const setDraftField = (userId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [userId]: {
        role: prev[userId]?.role ?? users.find((user) => user.id === userId)?.role ?? 'user',
        siteIds: prev[userId]?.siteIds ?? users.find((user) => user.id === userId)?.siteIds ?? [],
        [field]: value,
      },
    }));
  };

  const getDraft = (user) => ({
    role: drafts[user.id]?.role ?? user.role ?? 'user',
    siteIds: drafts[user.id]?.siteIds ?? user.siteIds ?? [],
  });

  const toggleFormSite = (siteId) => {
    setForm((prev) => ({
      ...prev,
      siteIds: prev.siteIds.includes(siteId)
        ? prev.siteIds.filter((id) => id !== siteId)
        : [...prev.siteIds, siteId],
    }));
  };

  const toggleDraftSite = (user, siteId) => {
    const draft = getDraft(user);
    const nextSiteIds = draft.siteIds.includes(siteId)
      ? draft.siteIds.filter((id) => id !== siteId)
      : [...draft.siteIds, siteId];
    setDraftField(user.id, 'siteIds', nextSiteIds);
  };

  const addUser = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/users/me/account-users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to add user');
      setUsers((prev) => [...prev, data.user].filter(Boolean));
      setForm(EMPTY_FORM);
      setMessage(data?.message || 'User added');
    } catch (err) {
      setError(err.message || 'Failed to add user');
    } finally {
      setSaving(false);
    }
  };

  const saveUser = async (user) => {
    const draft = getDraft(user);
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/users/me/account-users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(draft),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to update user');
      setUsers((prev) => prev.map((entry) => (entry.id === user.id ? data.user : entry)));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
      setMessage(data?.message || `Updated ${user.email}`);
    } catch (err) {
      setError(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const removeUser = async (user) => {
    const confirmed = window.prompt(`Type REMOVE ${user.email} to delete this user.`);
    if (confirmed !== `REMOVE ${user.email}`) return;

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/users/me/account-users/${user.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to delete user');
      setUsers((prev) => prev.filter((entry) => entry.id !== user.id));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
      setMessage(data?.message || `Removed ${user.email}`);
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 16, color: '#e5e7eb' }}>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 6px' }}>Users</h3>
        <div style={{ color: '#9ca3af', fontSize: 14 }}>
          Add account users, choose whether they join as a user or editor, and grant access site by site.
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, border: '1px solid #991b1b', background: 'rgba(127,29,29,0.2)' }}>
          {error}
        </div>
      )}
      {message && (
        <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, border: '1px solid #065f46', background: 'rgba(6,95,70,0.2)' }}>
          {message}
        </div>
      )}

      <form
        onSubmit={addUser}
        style={{ marginBottom: 24, padding: 16, borderRadius: 12, border: '1px solid #374151', background: '#111827' }}
      >
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Add User</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #374151', background: '#0f172a', color: '#e5e7eb' }}
          />
          <input
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
            style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #374151', background: '#0f172a', color: '#e5e7eb' }}
          />
          <select
            value={form.role}
            onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
            style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #374151', background: '#0f172a', color: '#e5e7eb' }}
          >
            <option value="user">User</option>
            <option value="editor">Editor</option>
          </select>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ marginBottom: 8, color: '#cbd5e1', fontSize: 14 }}>Site access</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {sortedSites.map((site) => (
              <label
                key={site.id}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 999, border: '1px solid #374151', background: '#0f172a' }}
              >
                <input
                  type="checkbox"
                  checked={form.siteIds.includes(site.id)}
                  onChange={() => toggleFormSite(site.id)}
                />
                <span>{site.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
          <button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Add User'}
          </button>
          <button type="button" onClick={loadUsers} disabled={loading || saving}>
            Refresh
          </button>
        </div>
      </form>

      {loading && <div style={{ color: '#9ca3af' }}>Loading users…</div>}

      {!loading && !users.length && (
        <div style={{ color: '#9ca3af' }}>No account users yet.</div>
      )}

      {!loading && users.map((user) => {
        const draft = getDraft(user);
        return (
          <div
            key={user.id}
            style={{ marginBottom: 12, padding: 16, borderRadius: 12, border: '1px solid #374151', background: '#111827' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{user.email}</div>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>
                  Added {user.created_at ? new Date(user.created_at).toLocaleString() : 'recently'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <select
                  value={draft.role}
                  onChange={(e) => setDraftField(user.id, 'role', e.target.value)}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #374151', background: '#0f172a', color: '#e5e7eb' }}
                >
                  <option value="user">User</option>
                  <option value="editor">Editor</option>
                </select>
                <button type="button" onClick={() => saveUser(user)} disabled={saving}>
                  Save
                </button>
                <button type="button" onClick={() => removeUser(user)} disabled={saving}>
                  Remove
                </button>
              </div>
            </div>

            <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {sortedSites.map((site) => (
                <label
                  key={`${user.id}:${site.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 999, border: '1px solid #374151', background: '#0f172a' }}
                >
                  <input
                    type="checkbox"
                    checked={draft.siteIds.includes(site.id)}
                    onChange={() => toggleDraftSite(user, site.id)}
                  />
                  <span>{site.name}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default UsersSettings;
